import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Reveal } from '@/components/generals/reveal'
import { Ruler } from '@/components/generals/ruler'
import { SectionLabel } from '@/components/generals/section-label'

import { EQUIPMENT } from '@/constants/content'

type GalleryItem = {
    src: string
    alt: string
    caption: string
    position: string
}

const GALLERY: Record<'lifts' | 'diagnostic' | 'brakes' | 'tools', GalleryItem> = {
    lifts: {
        src: '/images/atelier-pont.jpg',
        alt: 'Quatre ponts élévateurs alignés dans l’atelier, une citadine levée au premier plan',
        caption: 'Quatre ponts, sol époxy nettoyé chaque soir',
        position: '28% 55%'
    },
    diagnostic: {
        src: '/images/atelier-diagnostic.jpg',
        alt: 'Valise de diagnostic branchée sur la prise d’une voiture, courbes de mesure à l’écran',
        caption: 'Diagnostic électronique multimarque',
        position: '58% 62%'
    },
    brakes: {
        src: '/images/atelier-freinage.jpg',
        alt: 'Serrage au couple d’un disque de frein neuf avec une clé dynamométrique',
        caption: 'Freins serrés au couple constructeur',
        position: '45% 50%'
    },
    tools: {
        src: '/images/atelier-outillage.jpg',
        alt: 'Servante d’atelier ouverte, chaque outil rangé dans sa mousse découpée',
        caption: 'Chaque outil a sa place, et il y est',
        position: '50% 45%'
    }
}

const MOBILE_ORDER = [GALLERY.lifts, GALLERY.diagnostic, GALLERY.brakes, GALLERY.tools]

interface GalleryFigureProps {
    item: GalleryItem
    sizes: string
    className?: string
}

const GalleryFigure: FC<GalleryFigureProps> = ({ item, sizes, className }): ReactNode => {
    return (
        <figure className={cn('relative overflow-hidden rounded-lg bg-card', className)}>
            <Image
                src={item.src}
                alt={item.alt}
                fill
                sizes={sizes}
                className='object-cover'
                style={{ objectPosition: item.position }}
            />

            <figcaption className='label-mono absolute bottom-3 left-3 rounded-sm bg-ink/80 px-2.5 py-1.5 text-chalk'>
                {item.caption}
            </figcaption>
        </figure>
    )
}

export const WorkshopSection: FC = (): ReactNode => {
    return (
        <section id='atelier' data-theme='ink' aria-labelledby='atelier-title' className='overflow-hidden bg-background py-20 text-foreground lg:py-32'>
            <Container className='grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-10 xl:gap-16'>
                <div className='lg:col-span-7'>
                    <SectionLabel index='03'>L’atelier</SectionLabel>

                    <h2 id='atelier-title' className='heading mt-5 text-title'>
                        Un atelier où l’on pourrait manger par terre.
                    </h2>
                </div>

                <p className='text-lead text-muted-foreground lg:col-span-5'>
                    380&nbsp;m² sur l’île de Nantes, quatre ponts, l’outillage d’une concession et le rangement d’un horloger. Un atelier propre, c’est un travail propre.
                </p>
            </Container>

            <div className='mt-10 lg:hidden'>
                <ul
                    aria-label='Photos de l’atelier'
                    className='no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-5 px-5 pb-2 sm:scroll-px-8 sm:px-8'
                >
                    {MOBILE_ORDER.map(item => (
                        <li key={item.src} className='w-[82%] shrink-0 snap-start sm:w-[58%]'>
                            <GalleryFigure item={item} sizes='(min-width: 640px) 58vw, 82vw' className='aspect-[4/5]' />
                        </li>
                    ))}
                </ul>

                <p className='label-mono mt-3 px-5 text-muted-foreground sm:px-8'>
                    Faites glisser pour voir l’atelier
                </p>
            </div>

            <Container className='mt-16 hidden lg:block'>
                <Reveal className='grid h-[clamp(32rem,40vw,40rem)] grid-cols-12 grid-rows-2 gap-4'>
                    <GalleryFigure item={GALLERY.lifts} sizes='(min-width: 1440px) 900px, 62vw' className='col-span-8 row-span-2' />
                    <GalleryFigure item={GALLERY.diagnostic} sizes='(min-width: 1440px) 450px, 31vw' className='col-span-4' />
                    <GalleryFigure item={GALLERY.brakes} sizes='(min-width: 1440px) 450px, 31vw' className='col-span-4' />
                </Reveal>
            </Container>

            <Container className='mt-16 grid gap-10 lg:mt-20 lg:grid-cols-12 lg:gap-10 xl:gap-16'>
                <div className='lg:col-span-7'>
                    <h3 className='label-mono text-muted-foreground'>
                        Fiche technique de l’atelier
                    </h3>

                    <Ruler className='mt-4 text-border' />

                    <dl className='grid sm:grid-cols-2'>
                        {EQUIPMENT.map(item => (
                            <div key={item.figure} className='border-b border-border py-6 sm:odd:pr-8 sm:even:border-l sm:even:pl-8'>
                                <dt className='heading text-[1.625rem] leading-tight'>
                                    {item.figure}
                                </dt>

                                <dd className='mt-1.5 text-muted-foreground'>
                                    {item.label}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='hidden lg:col-span-5 lg:block'>
                    <GalleryFigure item={GALLERY.tools} sizes='(min-width: 1440px) 520px, 36vw' className='aspect-[4/5]' />
                </div>
            </Container>
        </section>
    )
}
